import type { ErrorActorEvent } from 'xstate'
import { fromPromise, sendParent, setup } from 'xstate'
import { softDeleteItemsFromMultiCart } from 'api/cart/softDeleteItemsFromMultiCart'
import { undoDeleteItemsFromMultiCart } from 'api/cart/undoDeleteItemsFromMultiCart'
import * as Analytics from 'analytics/analytics'
import { reportClientError } from 'services/errorReportingService'
import { errorSnackbar } from 'machines/cart/snackbarUtils'

type MultiCartDeleteItemMachineInput = {
  cartId: string
  itemIds: Array<string>
  currentStates: Record<string, App.MultiCartItemState>
}

type MultiCartDeleteItemMachineContext = {
  cartId: string
  itemIds: Array<string>
  historyStates: Record<string, App.MultiCartItemState>
}

type MultiCartDeleteItemMachineEvents =
  | { type: 'soft delete' }
  | { type: 'undo delete' }

const DEFAULT_USER_CART_DELETE_DELAY_MS = 5000

export const multiCartDeleteItemMachine = setup({
  types: {
    context: {} as MultiCartDeleteItemMachineContext,
    events: {} as MultiCartDeleteItemMachineEvents,
    input: {} as MultiCartDeleteItemMachineInput,
  },
  actors: {
    softDeleteItemsFromMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIdsToDelete: Array<string> } }) =>
      softDeleteItemsFromMultiCart(input.cartId, input.itemIdsToDelete),
    ),
    undoDeleteItemsFromMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIds: Array<string> } }) =>
      undoDeleteItemsFromMultiCart(input.cartId, input.itemIds),
    ),
  },
  actions: {
    markItemAsDeleted:
      sendParent(({ context }) => ({
        type: 'mark item as deleted',
        cartId: context.cartId,
        itemIds: context.itemIds,
      })),
    restoreItemStates:
      sendParent(({ context }) => ({
        type: 'restore item states',
        cartId: context.cartId,
        itemIds: context.itemIds,
        historyStates: context.historyStates,
      })),
    removeItemFromCart:
      sendParent(({ context }) => ({
        type: 'remove item from cart',
        cartId: context.cartId,
        itemIds: context.itemIds,
      })),
    trackUndoDeleteClickAnalyticsEvent: () => {
      Analytics.trackClientEvent({
        subject: 'undo_delete_item',
        action: 'click',
        category: 'multi_cart',
        type: 'interaction',
        options: {
          contextsToSkip: ['cart'], // Skip cart context to avoid event size limit (cart items make event too large)
        },
      })
    },
    showSoftDeleteErrorSnackbar: () => {
      errorSnackbar('Failed to soft delete items')
    },
    showUndoDeleteErrorSnackbar: () => {
      errorSnackbar('Failed to undo delete')
    },
    logErrorEvent: ({ event }: { event: MultiCartDeleteItemMachineEvents | ErrorActorEvent<Error, string> }, params?: { operation?: string }) => {
      // Only process ErrorActorEvent types
      if (!('error' in event)) return

      const operation = params?.operation || 'multiCartDeleteItem operation'
      reportClientError(event.error, {
        message: `MultiCartDeleteItem machine error: ${operation}`,
        actorId: event.actorId,
        stateMachineError: true,
      })
      console.error(`[MultiCartDeleteItem] ${operation} failed:`, event.error)
    },
  },
  delays: {
    deleteDelay: DEFAULT_USER_CART_DELETE_DELAY_MS,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiWAewDMAXAAgjCqbAG0AGAXUSgADnVi4muOviEgAHogCMAdgDMJRQFYALMoBMATgAcRg3s07tAGhABPJXvWLevZYt1HNANl6nlAX38bNCw8QlJ6ZgARDjAuCGoAV3wIOjZYrj5BJBBRcUlpWQUEbUUjElVVZV49Wu0vbUMVG3sESoMSPV9NA1VSt0VawOCMHAJiEkimGM5IajlYJnQuEnRmMAAnZHZZmfRbImoQsfDJxmmMyCzZPIkpGRzit3VeVUUyrx1eA2NrOyVNOovIYjKVPF5FD4AkEQMcwhNsOgNhAZnE5qlCGR8AA3OgAazAJDh41IiORqPiCAIuMwy3uWWuOVuBQeoGKg3qGi0vG02gsXgMZVULSU7hIBkaOlU3yMPLeqmGsNG8NJSJRlwSmw2dA2JGEFGWDB1qCJypJJDJ6tmECpOLotJZDIENzEd0Kj0QpS84t4DV8XmUJmBBhFCAAtICSCZVJoyrwdF0edpAjD8HR2PAcsTwi78vciogw15Q2H3IoKkYY5DXD8BZo9IrsxNyFRc27WfJPXpQ+89FHY7HqjGftCRqFzVMKZA2yyCyULJ09PpvG5NEZlKUe3pysHQWVvNXR0rx6dLVOIDP8x6EGZtCRgb6IcoN0u9Iot33VK-n9vvL4DEeTakBiYCXu6bIOEY3rfL02gGC4fSfJooaGB0vpvnyqgBkYigGCm-hAA */
  context: ({ input }: { input: MultiCartDeleteItemMachineInput }) => {
    return {
      cartId: input.cartId,
      itemIds: input.itemIds,
      historyStates: input.currentStates,
    }
  },
  initial: 'idle',
  states: {
    idle: {
      on: {
        'soft delete': {
          target: 'softDeleting',
        },
      },
    },
    softDeleting: {
      entry: ['markItemAsDeleted'],
      invoke: {
        src: 'softDeleteItemsFromMultiCart',
        input: ({ context }) => ({
          cartId: context.cartId,
          itemIdsToDelete: context.itemIds,
        }),
        onDone: {
          target: 'softDeletedConfirmed',
        },
        onError: {
          target: 'softDeletedError',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'softDeleteItemsFromMultiCart' } },
            'showSoftDeleteErrorSnackbar',
          ],
        },
      },
    },
    softDeletedConfirmed: {
      after: {
        deleteDelay: 'hardDeleted',
      },
      on: {
        'undo delete': {
          target: 'undoingDelete',
        },
      },
    },
    softDeletedError: {
      on: {
        'undo delete': {
          target: 'done',
          actions: ['restoreItemStates', 'trackUndoDeleteClickAnalyticsEvent'],
        },
      },
    },
    undoingDelete: {
      invoke: {
        src: 'undoDeleteItemsFromMultiCart',
        input: ({ context }) => ({
          cartId: context.cartId,
          itemIds: context.itemIds,
        }),
        onDone: {
          target: 'done',
          actions: ['restoreItemStates', 'trackUndoDeleteClickAnalyticsEvent'],
        },
        onError: {
          target: 'softDeletedConfirmed',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'undoDeleteItemsFromMultiCart' } },
            'showUndoDeleteErrorSnackbar',
          ],
        },
      },
    },
    hardDeleted: {
      entry: ['removeItemFromCart'],
      always: 'done',
    },
    done: {
      type: 'final',
    },
  },
})
